import { useEffect, useMemo, useRef, useState } from "react";
import type {
  HotelRoomRateMeta,
  HotelRoomRateSelection,
} from "@b2bportal/core-types";
import { getImageUrlsArray } from "@b2bportal/core-utilities";
import {
  LodgingMediaAssetEnum,
  type RoomProduct,
  type RoomInfoProducts,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import {
  type HotelRoomCardComponentProps,
  useMealPlanHelpers,
} from "@tenant/hotels";

import { useHotelRoomRates } from "../../../hooks";
import {
  BREAKFAST_MEAL_PLAN_KINDS,
  isFreeBreakfastAvailableInAllRoomProducts,
  isFreeBreakfastAvailableInRoomLevelAmenities,
} from "../../../hooks/lodgingFreeBreakfast";
import { getRoomLevelMealPlanKind } from "../../../hooks/lodgingMealPlans";

type RoomInfo = RoomInfoProducts["roomInfo"];
type RoomMedia = RoomInfo["media"][number];
type ProductWithRateId = Omit<RoomProduct, "rateId"> & {
  rateId: NonNullable<RoomProduct["rateId"]>;
};

type UseHotelRoomCardProps = {
  room: RoomInfoProducts;
  onRoomSelection: HotelRoomCardComponentProps["onRoomSelection"];
  nightCount: number;
  hotelHasPaidBreakfast?: boolean;
};

type UseHotelRoomCardContext = {
  room: RoomInfoProducts;
  amenities: RoomInfo["amenities"];
  description: RoomInfo["description"];
  detailsImage: RoomMedia | undefined;
  products: Array<ProductWithRateId>;
  rates: HotelRoomRateMeta[];
  selectedRate: HotelRoomRateMeta;
  selectedProduct: RoomProduct;
  images: string[];
  nightCount: number;
  mealPlanName: string | undefined;
};

export type UseHotelRoomCardReturn = {
  context: UseHotelRoomCardContext;
  handlers: {
    onRoomSelection: HotelRoomCardComponentProps["onRoomSelection"];
    setSelectedRate: (rate: HotelRoomRateMeta) => void;
    setSelectedProduct: (product: RoomProduct) => void;
    setSelectedRoom: (product: RoomProduct) => void;
  };
  text: {
    roomName: string | undefined;
    guests: string | undefined;
    beds: string;
    viewDetails: string;
    roomCTA: string;
  };
  rateSelection: HotelRoomRateSelection;
};

export function useHotelRoomCard({
  room,
  onRoomSelection,
  nightCount,
  hotelHasPaidBreakfast,
}: UseHotelRoomCardProps): UseHotelRoomCardReturn {
  const { t } = useI18nContext();

  const [selectedRoom, setSelectedRoom] = useState<RoomProduct | null>(null);

  const onRoomSelectionRef = useRef(onRoomSelection);
  onRoomSelectionRef.current = onRoomSelection;

  useEffect(() => {
    if (selectedRoom != null) {
      onRoomSelectionRef.current?.(selectedRoom);
    }
  }, [selectedRoom]);

  const rateSelection = useHotelRoomRates({
    room,
  });

  const { rates, selectedRate, setSelectedRate } = rateSelection;

  const {
    handlers: { getMealPlanNameFromMealPlanKind },
  } = useMealPlanHelpers();

  const detailsImage = room.roomInfo.media.find((media) => {
    return (
      media.LodgingMediaAsset === LodgingMediaAssetEnum.NonResizableImage ||
      media.LodgingMediaAsset === LodgingMediaAssetEnum.ResizableImage
    );
  });

  return useMemo(() => {
    const occupancy =
      room.roomInfo.maxAdults ?? selectedRate.product.bedTypes.occupancy;

    const products = room.products.filter(
      (product): product is ProductWithRateId => product.rateId != null
    );

    const context: UseHotelRoomCardContext = {
      room,
      amenities: room.roomInfo.amenities,
      description: room.roomInfo.description,
      detailsImage: detailsImage,
      products,
      rates,
      selectedRate,
      selectedProduct: selectedRate.product,
      images: getImageUrlsArray(room.roomInfo.media),
      nightCount,
      mealPlanName: (() => {
        const kind = getRoomLevelMealPlanKind(room);
        if (kind != null) {
          // MealPlanKind has no paid/free distinction — suppress breakfast
          // labels when the hotel-level amenity says breakfast is paid.
          if (hotelHasPaidBreakfast && BREAKFAST_MEAL_PLAN_KINDS.has(kind)) {
            return undefined;
          }
          return getMealPlanNameFromMealPlanKind(kind);
        }

        const freeBreakfastLabel = t(
          "core-hotels.availabilityPage.results.hotelCard.features.breakfastFree"
        );

        // Room-level amenities are authoritative provider data and take
        // precedence over the hotel-level PaidBreakfast signal — a premium
        // room can genuinely include breakfast even when the hotel default
        // is paid. This is NOT gated by hotelHasPaidBreakfast on purpose.
        if (isFreeBreakfastAvailableInRoomLevelAmenities(room.roomInfo)) {
          return freeBreakfastLabel;
        }

        // Product-level meal plan heuristic is unreliable for the paid/free
        // distinction, so we suppress it when the hotel says paid.
        if (
          !hotelHasPaidBreakfast &&
          isFreeBreakfastAvailableInAllRoomProducts(room)
        ) {
          return freeBreakfastLabel;
        }

        return undefined;
      })(),
    };

    const setSelectedProduct = (product: RoomProduct) => {
      const rate = rates.find(
        (r) => r.product.rateId?.value === product.rateId?.value
      );
      if (rate != null) {
        setSelectedRate(rate);
      }
    };

    const handlers = {
      onRoomSelection,
      setSelectedRate,
      setSelectedProduct,
      setSelectedRoom,
    };

    const text = {
      roomName: room.roomInfo.name
        ? (htmlDecode(room.roomInfo.name) ?? room.roomInfo.name)
        : undefined,
      guests:
        occupancy != null && occupancy !== 0
          ? t("core-hotels.roomSelection.roomFitsGuests", {
              count: occupancy,
            })
          : undefined,
      beds: room.roomInfo.beds.description,
      viewDetails: t("core-hotels.roomSelection.viewDetails"),
      roomCTA: t("core-hotels.shop.price.roomCTA"),
    };

    return { context, handlers, text, rateSelection };
  }, [
    room,
    detailsImage,
    hotelHasPaidBreakfast,
    rates,
    selectedRate,
    nightCount,
    onRoomSelection,
    setSelectedRate,
    setSelectedRoom,
    t,
    rateSelection,
    getMealPlanNameFromMealPlanKind,
  ]);
}
